import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Form, Grid, Icon, Modal } from "semantic-ui-react";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import CardNurseNoteUX from "./CardNurseNoteUX";
import ModProgressNoteForm from "./ModProgressNoteFormUX";
import ModAuthen from "react-lib/apps/common/ModAuthen";
import * as utils from "react-lib/utils";
import moment from "moment";

import ModMedReconcileAlert from "react-lib/apps/common/ModMedReconcileAlert";
import CardSelectDoctorOrder from "react-lib/apps/PTM/CardSelectDoctorOrder";
import CardSelectDoctorOrderNote from "react-lib/apps/HISV3/PTM/CardSelectDoctorOrderNote";
import { useIntl } from "react-intl";
import { formatUTCtoBEString } from "react-lib/utils/dateUtils";

type Credentials = {
  username: string;
  password: string;
};

const CARD_NURSE_NOTE = "cardNurseNote";
const CardNurseNote = (props: any) => {
  const intl = useIntl();
  const [openModAuthen, setOpenModAuthen] = useState<boolean>(false);
  const [action, setAction] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [openModSelectDoctorOrder, setOpenModSelectDoctorOrder] = useState<boolean>(false);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  useEffect(() => {
    return () => {
      props.onEvent({
        message: "HandleNurseNote",
        params: {
          action: "clear",
        },
      });
    };
  }, []);

  useEffect(() => {
    props.onEvent({
      message: "HandleNurseNote",
      params: {
        action: "search",
        encounterId: props.selectedEncounter?.id,
        card: CARD_NURSE_NOTE,
      },
    });
  }, [props.selectedEncounter]);

  useEffect(() => {
    setOpenModAuthen(false);
  }, [props.openModNurseNoteForm]);

  const nurseNoteFocusOptions = useMemo(() => {
    const focus = props.selectedNurseNote.focus;
    let optionData = (props.nurseNoteFocusOptions || [])?.filter((item: any) => item.value !== "");
    const checkOptions = optionData.filter((item: any) => item.value === focus);

    if (checkOptions.length === 0) {
      optionData.push({ key: focus, value: focus, text: focus });
    }

    return optionData;
  }, [props.nurseNoteFocusOptions, props.selectedNurseNote]);

  const handleConfirmSaveNurseNote = ({ username, password }: Credentials) => {
    props.onEvent({
      message: "HandleNurseNote",
      params: {
        action: "save",
        date: utils.formatDatetime(moment()).replace(" ", "-"),
        encounterId: props.selectedEncounter?.id,
        username,
        password,
        card: CARD_NURSE_NOTE,
      },
    });
  };

  const handleConfirmRemoveNurseNote = ({ username, password }: Credentials) => {
    props.onEvent({
      message: "HandleNurseNote",
      params: {
        action: "remove",
        encounterId: props.selectedEncounter?.id,
        username,
        password,
        card: CARD_NURSE_NOTE,
      },
    });
  };

  const handleChecked = (checked: boolean | undefined, item: any) => {
    const ids: number[] = item.id_list || [item.id];

    if (checkedIds.some((id) => ids.includes(id))) {
      setCheckedIds(checkedIds.filter((id) => !ids.includes(id)));
    } else {
      setCheckedIds([...checkedIds, ...ids]);
    }
  };

  const decodeHTMLEntities = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const selectedNurseNote = useMemo(() => {
    return {
      ...props.selectedNurseNote,
      diagnosis: decodeHTMLEntities(props.selectedNurseNote.diagnosis),
      goal: decodeHTMLEntities(props.selectedNurseNote.goal),
      plan: decodeHTMLEntities(props.selectedNurseNote.plan),
    };
  }, [props.selectedNurseNote]);

  const nurseNoteList = useMemo(() => {
    return (props.nurseNoteFilter?.nurseNoteList || [])
      .sort((a: any, b: any) => (a.edited < b.edited ? 1 : -1))
      .map((item: any, index: number) => {
        let createdBEString = formatUTCtoBEString(item.created || "");
        let editedBEString = formatUTCtoBEString(item.edited || "");
        return {
          ...item,
          formatted_date: (
            <Form>
              <Form.Group inline>
                <Form.Field width={13}>
                  <div>{`${createdBEString?.[0] || ""} [${createdBEString?.[1] || "00:00"}]`}</div>
                </Form.Field>
              </Form.Group>
              {["IPD", "SS"].includes(props.selectedEncounter.type) && (
                <Form.Group inline>
                  <Form.Field width={3}></Form.Field>
                  <Form.Field width={13}>
                    <div>{item.working_shift || ""}</div>
                  </Form.Field>
                </Form.Group>
              )}
              <Form.Group inline>
                <Form.Field width={13}>
                  <div>{`แก้ไขครั้งล่าสุด`}</div>
                </Form.Field>
              </Form.Group>
              <Form.Group inline>
                <Form.Field width={13}>
                  <div>{`${editedBEString?.[0] || ""} [${editedBEString?.[1] || "00:00"}]`}</div>
                </Form.Field>
              </Form.Group>
            </Form>
          ),
          focus: (
            <Form>
              <Form.Group inline>
                <Form.Field>
                  <div>{item.focus || ""}</div>
                </Form.Field>
              </Form.Group>
              <Form.Group inline>
                <Form.Field>
                  <div>{item.doctor_order_detail || ""}</div>
                </Form.Field>
              </Form.Group>
            </Form>
          ),
          progress_note: (
            <div style={{ wordWrap: "break-word", padding: "2px" }}>
              <Grid divided="vertically">
                <Grid.Row columns={2}>
                  <Grid.Column width={2} style={{ fontWeight: "bold", textAlign: "center" }}>
                    A:
                  </Grid.Column>
                  <Grid.Column width={14}>{decodeHTMLEntities(item.diagnosis)}</Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2}>
                  <Grid.Column width={2} style={{ fontWeight: "bold", textAlign: "center" }}>
                    I:
                  </Grid.Column>
                  <Grid.Column width={14}>{decodeHTMLEntities(item.plan)}</Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2}>
                  <Grid.Column width={2} style={{ fontWeight: "bold", textAlign: "center" }}>
                    E:
                  </Grid.Column>
                  <Grid.Column width={14}>{decodeHTMLEntities(item.goal)}</Grid.Column>
                </Grid.Row>
              </Grid>
            </div>
          ),
          _action: (
            <>
              <Button
                id={`CardNurseNote-Item-Button-Edit-${index}`}
                color="yellow"
                icon="pencil"
                onClick={() => {
                  setAction("edit");
                  props.onEvent({
                    message: "HandleNurseNote",
                    params: {
                      action: "edit",
                      card: CARD_NURSE_NOTE,
                      selectedNurseNote: item,
                    },
                  });
                }}
              />
              <Button
                id={`CardNurseNote-Item-Button-Delete-${index}`}
                color="red"
                icon="trash"
                onClick={() => {
                  props.setProp("selectedNurseNote", item);
                  // setAction("remove");
                  setOpenModAuthen(true);
                }}
              />
            </>
          ),
        };
      });
  }, [props.nurseNoteFilter?.nurseNoteList, checkedIds]);

  const handleOpenSelectDoctorOrder = () => {
    setOpenModSelectDoctorOrder(true);
  };

  console.log("Card Nurse Note: ", props);

  const handleCheckAll = (e: any, v: any) => {
    if (v.checked) {
      let allId = nurseNoteList.map((item: any) => {
        return item.id;
      });

      setCheckedIds(allId);
    } else {
      setCheckedIds([]);
    }
  };

  const columnsNurseNote = [
    {
      Header: (
        <Checkbox
          checked={checkedIds.length === nurseNoteList.length && nurseNoteList.length > 0}
          onChange={handleCheckAll}
        />
      ),
      width: 80,
      Cell: (row: any) => {
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Checkbox
              checked={checkedIds.includes(row.original.id)}
              onChange={(e, v) => {
                handleChecked(v.checked, row.original);
              }}
            />
          </div>
        );
      },
    },
    {
      Header: "วันที่และเวลาที่สร้าง",
      accessor: "formatted_date",
      width: 200,
    },
    {
      Header: "ผู้บันทึก",
      accessor: "nurse",
      width: 200,
    },
    {
      Header: "Focus",
      accessor: "focus",
    },
    {
      Header: "Progress Note A:Assessment I:Intervention E:Evaluation",
      accessor: "progress_note",
    },
    {
      Header: "",
      accessor: "_action",
      width: 100,
    },
  ];

  const handleChangeData = (e: any, v: any) => {
    props.setProp(`selectedNurseNote.${v.name}`, v.value);
  };

  return (
    <>
      <SnackMessage
        onEvent={props.onEvent}
        onClose={async () => {
          props.setProp(`errorMessage.${CARD_NURSE_NOTE}`, null);
          props.setProp(`successMessage.${CARD_NURSE_NOTE}`, null);
        }}
        error={props.errorMessage?.[CARD_NURSE_NOTE]}
        success={props.successMessage?.[CARD_NURSE_NOTE]}
        languageUX={props.languageUX}
      />
      <CardNurseNoteUX
        // data
        selectedStartDate={props.nurseNoteFilter?.startDate}
        selectedEndDate={props.nurseNoteFilter.endDate}
        nurseNoteList={nurseNoteList}
        columnsNurseNote={columnsNurseNote}
        showPrint={checkedIds.length === 0}
        loadingPrint={!!props.buttonLoadCheck?.[`${CARD_NURSE_NOTE}_PRINT`]}
        selectedNurseNote={props.selectedNurseNote}
        // function
        openModalNote={() => {
          setAction("add");
          props.onEvent({
            message: "HandleNurseNote",
            params: { action: "new", card: CARD_NURSE_NOTE },
          });
        }}
        nurseNoteLoading={props.nurseNoteLoading}
        nurseNoteSearch={() => {
          props.onEvent({
            message: "HandleNurseNote",
            params: {
              action: "search",
              card: CARD_NURSE_NOTE,
              encounterId: props.selectedEncounter?.id,
            },
          });
        }}
        onSetStartDate={(date: string) => {
          props.setProp("nurseNoteFilter.startDate", date);
        }}
        onSetEndDate={(date: string) => {
          props.setProp("nurseNoteFilter.endDate", date);
        }}
        handleNurseNoteReport={() => {
          props.onEvent({
            message: "handleNurseNoteReport",
            params: {
              card: CARD_NURSE_NOTE,
              encounter: props.selectedEncounter?.id,
              checkedIds: checkedIds,
            },
          });
        }}
        onSelectedRow={(state: any, row: any, column: any, instance: any) => {
          return {
            onClick: () => {
              if (row.original) {
                let nurseNoteData = (props.nurseNoteFilter?.nurseNoteList || []).find(
                  (items: any) => items.id === row.original?.id
                );
                props.setProp("selectedNurseNote", nurseNoteData);
              }
            },
            className:
              row?.original && row?.original?.id === props.selectedNurseNote?.id
                ? "blueSelectedRow"
                : "",
            style: {
              cursor: "pointer",
            },
          };
        }}
        isNurseForm={props.isNurseForm}
        languageUX={props.languageUX}
      />
      <ModAuthen
        titleName={intl.formatMessage({ id: "ระบุ Username และ Password เพื่อลบ" })}
        titleColor={"red"}
        open={openModAuthen}
        onDeny={() => setOpenModAuthen(false)}
        // error={props.authenError}
        onApprove={({ username, password }: Credentials) => {
          handleConfirmRemoveNurseNote({ username, password });
          setOpenModAuthen(false);
        }}
      />

      <ModMedReconcileAlert
        onEvent={props.onEvent}
        setProp={props.setProp}
        django={props.django}
        medReconcileCheck={props.medReconcileCheck}
        medReconcileIndex={props.medReconcileIndex}
        open={props.medReconcileCheck?.open_card_med_reconciliation}
        languageUX={props.languageUX}
      />

      <Modal open={props.openModNurseNoteForm}>
        <ModProgressNoteForm
          closeIcon={
            <Icon
              name="cancel"
              color="red"
              size="large"
              style={{ margin: "-30px -30px 0px 0px", cursor: "pointer" }}
              onClick={() => {
                props.setProp("openModNurseNoteForm", false);
              }}
            />
          }
          onChangeData={handleChangeData}
          dateCreate={utils.formatDatetime(moment())}
          username={username}
          password={password}
          onChangeUsername={(e: any) => {
            setUsername(e.target.value);
          }}
          onChangePassword={(e: any) => {
            setPassword(e.target.value);
          }}
          selectedNurseNote={selectedNurseNote}
          setProp={props.setProp}
          django={props.django}
          handleSaveNurseNote={() => {
            // setOpenModAuthen(true);
            if (action === "add" || action === "edit") {
              handleConfirmSaveNurseNote({ username, password });
            } else if (action === "remove") {
              handleConfirmRemoveNurseNote({ username, password });
            }
            setUsername("");
            setPassword("");
          }}
          nurseNoteFocusOptions={nurseNoteFocusOptions}
          onChangeDropdown={(e: any, data: any) => {
            props.onEvent({
              message: "HandleNurseNote",
              params: {
                action: "change_option",
                data: data.value,
                card: CARD_NURSE_NOTE,
              },
            });
          }}
          handleAddNurseNoteFocusOptions={(e: any, data: any) => {
            props.onEvent({
              message: "HandleNurseNote",
              params: {
                action: "add_option",
                data: data.value,
                card: CARD_NURSE_NOTE,
              },
            });
          }}
          isOPDEncounter={props.selectedEncounter?.type === "OPD"}
          onOpenSelectDoctorOrder={handleOpenSelectDoctorOrder}
          languageUX={props.languageUX}
        />
      </Modal>

      <Modal
        open={openModSelectDoctorOrder}
        onClose={() => setOpenModSelectDoctorOrder(false)}
        size="large"
      >
        <CardSelectDoctorOrderNote
          onEvent={props.onEvent}
          setProp={props.setProp}
          onClose={() => setOpenModSelectDoctorOrder(false)}
          nurseNoteSelectDoctorOrderNote={props.nurseNoteSelectDoctorOrderNote}
          buttonLoadCheck={props.buttonLoadCheck}
          selectedNurseNote={props.selectedNurseNote}
        />
        {/* <CardSelectDoctorOrder
          controller={props.nurseNoteController}
          patientData={props.patientData}
          toggleable={false}
          encounter={props.selectedEncounter?.id}
          onClose={() => setOpenModSelectDoctorOrder(false)}
          // onSelect={handleSelectDoctorOrder}
          languageUX={props.languageUX}
        /> */}
      </Modal>
    </>
  );
};

export default CardNurseNote;
